import { useTheme } from "@mui/material";
import DSBox from "./DSBox";
import DSTypography from "./DSTypography";

export type SegmentedTabOption<T extends string = string> = {
  label: string;
  value: T;
  badge?: number | boolean;
  disabled?: boolean;
};

export type DSSegmentedTabsProps<T extends string = string> = {
  options: SegmentedTabOption<T>[];
  value: T | undefined;
  onChange: (value: T | undefined) => void;
  /** clicking the active tab deselects it (legacy toggle-off behavior) */
  allowEmpty?: boolean;
  size?: "small" | "medium";
};

/**
 * Studio-style segmented control: a rounded track with a sliding pill
 * highlight. Replaces the legacy "row of toggle buttons" pattern.
 * Horizontally scrollable on small screens.
 */
const DSSegmentedTabs = <T extends string = string>({
  options,
  value,
  onChange,
  allowEmpty = false,
  size = "medium",
}: DSSegmentedTabsProps<T>) => {
  const { palette } = useTheme();
  const pad = size === "small" ? "6px 14px" : "8px 20px";

  return (
    <DSBox
      role="tablist"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        p: 0.5,
        borderRadius: "999px",
        backgroundColor: palette.white[5],
        border: `1px solid ${palette.white[8]}`,
        maxWidth: "100%",
        overflowX: "auto",
        "&::-webkit-scrollbar": { display: "none" },
      }}
    >
      {options.map((opt) => {
        const selected = opt.value === value;
        return (
          <DSBox
            key={opt.value}
            role="tab"
            aria-selected={selected}
            onClick={() => {
              if (opt.disabled) return;
              if (selected) {
                if (allowEmpty) onChange(undefined);
                return;
              }
              onChange(opt.value);
            }}
            sx={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              gap: 1,
              padding: pad,
              borderRadius: "999px",
              cursor: opt.disabled ? "not-allowed" : "pointer",
              whiteSpace: "nowrap",
              userSelect: "none",
              backgroundColor: selected ? palette.primary.main : "transparent",
              transition: "background-color 0.2s ease, color 0.2s ease",
              "&:hover": {
                backgroundColor: selected
                  ? palette.primary.main
                  : palette.white[8],
              },
            }}
          >
            <DSTypography
              variant="subtitle2"
              sx={{
                fontSize: size === "small" ? "12px" : "13px",
                lineHeight: 1.2,
                color: opt.disabled
                  ? palette.text.disabled
                  : selected
                    ? palette.primary.contrastText
                    : palette.text.secondary,
              }}
            >
              {opt.label}
            </DSTypography>
            {opt.badge ? (
              <DSBox
                sx={{
                  minWidth: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: selected
                    ? palette.primary.contrastText
                    : palette.error.main,
                  ...(typeof opt.badge === "number" && {
                    minWidth: 18,
                    height: 18,
                    borderRadius: "9px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: 0.5,
                    fontSize: "11px",
                    fontFamily: "Gilroy-SemiBold",
                    color: selected ? palette.primary.main : "#fff",
                  }),
                }}
              >
                {typeof opt.badge === "number" ? opt.badge : null}
              </DSBox>
            ) : null}
          </DSBox>
        );
      })}
    </DSBox>
  );
};

export default DSSegmentedTabs;
